'use client';

import { Popover as BasePopover } from '@base-ui/react/popover';
import { useMemo } from 'react';

import { useSyncUncontrolledState } from '../../internal/hooks/useSyncUncontrolledState/useSyncUncontrolledState';
import {
  type DeprecatedPlacementValue,
  type PlacementValue,
} from '../../internal/types/placement';
import { type Component, type ComponentProps } from '../../types/component';
import {
  PopoverContext,
  type PopoverContextProps,
} from './internal/PopoverContext';

type ModalBehaviour = 'none' | 'modal' | 'trap-focus';

interface PopoverBaseProps {
  /**
   * Sets the Popover open/closed state (controlled).
   */
  isOpen?: boolean;
  /**
   * Whether the Popover is open by default (uncontrolled).
   */
  defaultOpen?: boolean;
  /**
   * Handler that is called when the Popover's open state changes.
   */
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * How the popover behaves when open.
   * - `'none'`: user can interact with the rest of the document.
   * - `'modal'`: scroll lock + outside pointer interactions disabled.
   * - `'trap-focus'`: focus is trapped inside the popup, but page scroll and outside pointer interactions remain enabled.
   *
   * @default 'none'
   */
  modalBehaviour?: ModalBehaviour;
  /**
   * `Popover.Trigger` + `Popover.Popup`.
   */
  children?: React.ReactNode;
  /**
   * @deprecated Use `modalBehaviour` instead.
   *
   * @example
   * // Before
   * <Popover isModal>...</Popover>
   *
   * // After
   * <Popover modalBehaviour="modal">...</Popover>
   */
  isModal?: boolean;
  /**
   * @deprecated Move to `Popover.Trigger`'s `enableHover` prop.
   *
   * @example
   * // Before
   * <Popover isHoverable>...</Popover>
   *
   * // After
   * <Popover>
   *   <Popover.Trigger enableHover render={...} />
   *   ...
   * </Popover>
   */
  isHoverable?: boolean;
  /**
   * @deprecated Move to `Popover.Popup`'s `placement` prop.
   *
   * @example
   * // Before
   * <Popover placement="top">...</Popover>
   *
   * // After
   * <Popover>
   *   <Popover.Popup placement="top">...</Popover.Popup>
   * </Popover>
   */
  placement?: PlacementValue | DeprecatedPlacementValue;
  /**
   * @deprecated Move to `Popover.Popup`'s `offset` prop.
   *
   * @example
   * // Before
   * <Popover offset={20}>...</Popover>
   *
   * // After
   * <Popover>
   *   <Popover.Popup offset={20}>...</Popover.Popup>
   * </Popover>
   */
  offset?: number;
  /**
   * @deprecated Move to `Popover.Popup`'s `disableFlip` prop. Note: inverted — `flip={false}` becomes `disableFlip`.
   *
   * @example
   * // Before
   * <Popover flip={false}>...</Popover>
   *
   * // After
   * <Popover>
   *   <Popover.Popup disableFlip>...</Popover.Popup>
   * </Popover>
   */
  flip?: boolean;
  /**
   * @deprecated Move to `Popover.Popup`'s `disableShift` prop. Note: inverted — `shift={false}` becomes `disableShift`.
   *
   * @example
   * // Before
   * <Popover shift={false}>...</Popover>
   *
   * // After
   * <Popover>
   *   <Popover.Popup disableShift>...</Popover.Popup>
   * </Popover>
   */
  shift?: boolean;
  /**
   * @deprecated Move to `Popover.Popup`'s `mountStrategy` prop. `keepMounted={true}` maps to `mountStrategy="eager"`.
   *
   * @example
   * // Before
   * <Popover keepMounted>...</Popover>
   *
   * // After
   * <Popover>
   *   <Popover.Popup mountStrategy="eager">...</Popover.Popup>
   * </Popover>
   */
  keepMounted?: boolean;
  /**
   * @deprecated Move to `Popover.Popup`'s `width` prop. `matchTriggerWidth={true}` maps to `width="trigger"`.
   *
   * @example
   * // Before
   * <Popover matchTriggerWidth>...</Popover>
   *
   * // After
   * <Popover>
   *   <Popover.Popup width="trigger">...</Popover.Popup>
   * </Popover>
   */
  matchTriggerWidth?: boolean;
  /**
   * @deprecated Move to `Popover.Popup`'s `disablePortal` prop.
   *
   * @example
   * // Before
   * <Popover disablePortal>...</Popover>
   *
   * // After
   * <Popover>
   *   <Popover.Popup disablePortal>...</Popover.Popup>
   * </Popover>
   */
  disablePortal?: boolean;
}

type PopoverProps = ComponentProps<PopoverBaseProps>;

type PopoverComponent = Component<PopoverBaseProps>;

function mapModalBehaviour(
  modalBehaviour: ModalBehaviour | undefined,
  isModal: boolean | undefined
): boolean | 'trap-focus' {
  if (modalBehaviour !== undefined) {
    if (modalBehaviour === 'none') {
      return false;
    }
    if (modalBehaviour === 'modal') {
      return true;
    }
    return 'trap-focus';
  }
  return isModal ?? false;
}

const Popover: PopoverComponent = (props) => {
  const {
    isOpen,
    defaultOpen = false,
    onOpenChange,
    modalBehaviour,
    isModal,
    isHoverable,
    placement,
    offset,
    flip,
    shift,
    keepMounted,
    matchTriggerWidth,
    disablePortal,
    children,
  } = props;

  // Base UI's Root owns the authoritative open state; we sync an uncontrolled
  // mirror so sub-components (e.g. `Popover.Close`, whose Base UI state object
  // is empty) can read it via `PopoverContext`.
  const [uncontrolledOpen, setUncontrolledOpen] = useSyncUncontrolledState(
    defaultOpen,
    onOpenChange
  );
  const finalIsOpen = isOpen ?? uncontrolledOpen;

  const contextValue = useMemo<PopoverContextProps>(
    () => ({
      placement,
      offset,
      disableFlip: flip === false ? true : undefined,
      disableShift: shift === false ? true : undefined,
      mountStrategy:
        keepMounted === true
          ? 'eager'
          : keepMounted === false
            ? 'lazy'
            : undefined,
      width:
        matchTriggerWidth === true
          ? 'trigger'
          : matchTriggerWidth === false
            ? 'auto'
            : undefined,
      disablePortal,
      enableHover: isHoverable,
      isOpen: finalIsOpen,
    }),
    [
      placement,
      offset,
      flip,
      shift,
      keepMounted,
      matchTriggerWidth,
      disablePortal,
      isHoverable,
      finalIsOpen,
    ]
  );

  return (
    <PopoverContext.Provider value={contextValue}>
      <BasePopover.Root
        open={isOpen}
        defaultOpen={defaultOpen}
        onOpenChange={setUncontrolledOpen}
        modal={mapModalBehaviour(modalBehaviour, isModal)}
      >
        {children}
      </BasePopover.Root>
    </PopoverContext.Provider>
  );
};

Popover.displayName = 'Popover';

export { Popover, type PopoverProps, type PopoverBaseProps };
